import {
  getPlaces,
  getAccommodation,
  getTravel,
  getDays,
  getTrip,
  updatePlace,
  updateAccommodation,
  updateTravel,
  updateDay
} from '../firebase/firestoreService';
import { tripLinkAuditService } from './tripLinkAuditService';
import { linkRegistryConfigService } from './linkRegistryConfigService';
import { shouldRouteThroughRegistry } from '../utils/linkRegistryModeUtils';
import { connectionRegistryService } from './connectionRegistryService';
import { documentService } from '@trip-planner-monorepo/documents';
import { LINK_TYPES } from '../config/entityTypeRegistry';
import { resolvePlaceIdFromReference } from '../utils/placeReferenceUtils';

/**
 * Service for performing health checks on trip data.
 * Identifies issues such as missing references, broken links, date conflicts, and orphaned entities.
 */
export const healthCheckService = {
  /**
     * Runs a comprehensive health check on the specified trip.
     * @param {string} userId - The ID of the user.
     * @param {string} tripId - The ID of the trip.
     * @param {function} onProgress - Callback for progress updates (optional).
     * @returns {Promise<Array>} A list of identified issues.
     */
  async runHealthCheck(userId, tripId, onProgress = () => { }) {
    console.log(`Starting health check for trip ${tripId}`);
    const issues = [];

    try {
      // 1. Fetch all data
      onProgress('Fetching trip data...', 10);
      const [trip, places, accommodations, travelItems, days, documents] = await Promise.all([
        getTrip(userId, tripId),
        getPlaces(userId, tripId),
        getAccommodation(userId, tripId),
        getTravel(userId, tripId),
        getDays(userId, tripId),
        documentService.fetchDocuments(userId, tripId).catch(() => []) // Graceful fallback if documents fail
      ]);

      if (!trip) {
        throw new Error('Trip not found');
      }

      // Create lookup maps for faster access
      const placeMap = new Map(places.map(p => [p.id, p]));
      const accommodationMap = new Map(accommodations.map(a => [a.id, a]));
      const travelMap = new Map(travelItems.map(t => [t.id, t]));
      const dayMap = new Map(days.map(d => [d.id, d]));
      const documentMap = new Map(documents.map(d => [d.firestoreId || d.id, d]));


      // Runs a health-check section defensively: a thrown error (malformed legacy
      // data, an unexpected shape) is logged and skipped rather than aborting the
      // whole scan, so later sections (including the registry integrity pass) still run.
      const runSection = (label, fn) => {
        try {
          fn();
        } catch (sectionError) {
          console.warn(`Health check: ${label} failed, skipping this section:`, sectionError);
        }
      };

      // 2. Check for Broken Place Links in Accommodations
      onProgress('Checking accommodation links...', 30);
      runSection('accommodation broken-link check', () => {
        accommodations.forEach(acc => {
          if (acc.placeReference) {
            const placeId = resolvePlaceIdFromReference(acc.placeReference);

            if (placeId && !placeMap.has(placeId)) {
              issues.push({
                id: `broken-link-acc-${acc.id}`,
                title: 'Broken Place Link',
                description: `Accommodation "${acc.summary || 'Unnamed'}" links to a missing place.`,
                type: 'broken-link',
                severity: 'medium',
                entityType: 'accommodation',
                entityId: acc.id,
                canAutoFix: true,
                details: { field: 'placeReference', invalidId: placeId }
              });
            }
          }
        });
      });

      // 3. Check for Broken Place Links in Travel
      onProgress('Checking travel links...', 50);
      runSection('travel broken-link check', () => {
        travelItems.forEach(item => {
          if (item.departurePlace && !placeMap.has(item.departurePlace)) {
            issues.push({
              id: `broken-link-travel-dep-${item.id}`,
              title: 'Broken Departure Place Link',
              description: `Travel "${item.summary || 'Unnamed'}" links to a missing departure place.`,
              type: 'broken-link',
              severity: 'medium',
              entityType: 'travel',
              entityId: item.id,
              canAutoFix: true,
              details: { field: 'departurePlace', invalidId: item.departurePlace }
            });
          }
          if (item.arrivalPlace && !placeMap.has(item.arrivalPlace)) {
            issues.push({
              id: `broken-link-travel-arr-${item.id}`,
              title: 'Broken Arrival Place Link',
              description: `Travel "${item.summary || 'Unnamed'}" links to a missing arrival place.`,
              type: 'broken-link',
              severity: 'medium',
              entityType: 'travel',
              entityId: item.id,
              canAutoFix: true,
              details: { field: 'arrivalPlace', invalidId: item.arrivalPlace }
            });
          }
        });
      });

      // 4. Check for Orphaned Places
      onProgress('Checking for orphaned places...', 70);

      // Build the set of entity ids that participate in an active registry link, so a
      // place linked via the registry (registry_only / dual_write trips) is not falsely
      // reported as orphaned. The legacy placeReference/travel checks below still cover
      // legacy_only trips.
      const registryLinkedEntityIds = new Set();
      // Set of `${idA}|${idB}` (sorted) for each active link pair, so reciprocity checks
      // (e.g. one-way document links) can see a registry link that isn't in legacy connections[].
      const registryLinkedPairs = new Set();
      // Full active-link list, reused below for the registry integrity pass (Issue 12):
      // hotels with a city and no located_in row, and links pointing at deleted entities.
      let allActiveLinks = [];
      try {
        const allLinks = await connectionRegistryService.getAllLinks(userId, tripId);
        allActiveLinks = (allLinks || []).filter(link => (link.linkLifecycle ?? 'active') === 'active');
        (allLinks || []).forEach(link => {
          if ((link.linkLifecycle ?? 'active') !== 'active') return;
          const ids = [link.entityLow?.id, link.entityHigh?.id].filter(Boolean);
          ids.forEach(id => registryLinkedEntityIds.add(id));
          if (ids.length === 2) {
            registryLinkedPairs.add([...ids].sort().join('|'));
          }
        });
      } catch (error) {
        console.warn('healthCheckService: could not read connection registry links for orphan/link checks', error);
      }

      runSection('orphaned-place check', () => {
        places.forEach(place => {
          let isReferenced = false;

          // Check accommodations
          for (const acc of accommodations) {
            const accPlaceId = resolvePlaceIdFromReference(acc.placeReference);
            if (accPlaceId === place.id) {
              isReferenced = true;
              break;
            }
          }

          // Check travel
          if (!isReferenced) {
            for (const item of travelItems) {
              if (item.departurePlace === place.id || item.arrivalPlace === place.id) {
                isReferenced = true;
                break;
              }
            }
          }

          // Check the connection registry — covers registry_only / dual_write trips where
          // the link lives only in the registry (not in legacy placeReference fields).
          if (!isReferenced && registryLinkedEntityIds.has(place.id)) {
            isReferenced = true;
          }

          if (!isReferenced) {
            issues.push({
              id: `orphaned-place-${place.id}`,
              title: 'Orphaned Place',
              description: `Place "${place.name}" is not linked to any other item. You may wish to link it to one or more Day, Accommodation or Travel items.`,
              type: 'orphaned-entity',
              severity: 'low',
              entityType: 'place',
              entityId: place.id,
              canAutoFix: false // User decision needed (delete or keep)
            });
          }
        });
      });

      // 5. Check for Orphaned Entity References in Day Content
      onProgress('Checking day content for orphaned references...', 75);
      runSection('day content orphaned-reference check', () => {
        days.forEach(day => {
          // Combine content and contentBlocks arrays for comprehensive checking
          const allBlocks = [
            ...(Array.isArray(day.content) ? day.content : []),
            ...(Array.isArray(day.contentBlocks) ? day.contentBlocks : [])
          ];

          allBlocks.forEach(block => {

            // Check for orphaned travel references
            if (block.type === 'travel_reference' && block.travelId) {
              if (!travelMap.has(block.travelId)) {
                issues.push({
                  id: `orphaned-travel-ref-${day.id}-${block.id || block.travelId}`,
                  title: 'Orphaned Travel Link in Day',
                  description: `Day "${day.title || `Day ${day.dayNumber || day.order + 1}`}" contains a link to a deleted travel item.`,
                  type: 'orphaned-day-reference',
                  severity: 'medium',
                  entityType: 'day',
                  entityId: day.id,
                  canAutoFix: true,
                  details: {
                    blockType: 'travel_reference',
                    blockId: block.id,
                    invalidId: block.travelId
                  }
                });
              }
            }

            // Check for orphaned accommodation references
            if (block.type === 'accommodation_reference' && block.accommodationId) {
              if (!accommodationMap.has(block.accommodationId)) {
                issues.push({
                  id: `orphaned-acc-ref-${day.id}-${block.id || block.accommodationId}`,
                  title: 'Orphaned Accommodation Link in Day',
                  description: `Day "${day.title || `Day ${day.dayNumber || day.order + 1}`}" contains a link to a deleted accommodation.`,
                  type: 'orphaned-day-reference',
                  severity: 'medium',
                  entityType: 'day',
                  entityId: day.id,
                  canAutoFix: true,
                  details: {
                    blockType: 'accommodation_reference',
                    blockId: block.id,
                    invalidId: block.accommodationId
                  }
                });
              }
            }

            // Check for orphaned place references
            if (block.type === 'place_reference' && block.placeId) {
              if (!placeMap.has(block.placeId)) {
                issues.push({
                  id: `orphaned-place-ref-${day.id}-${block.id || block.placeId}`,
                  title: 'Orphaned Place Link in Day',
                  description: `Day "${day.title || `Day ${day.dayNumber || day.order + 1}`}" contains a link to a deleted place.`,
                  type: 'orphaned-day-reference',
                  severity: 'medium',
                  entityType: 'day',
                  entityId: day.id,
                  canAutoFix: true,
                  details: {
                    blockType: 'place_reference',
                    blockId: block.id,
                    invalidId: block.placeId
                  }
                });
              }
            }

            // Check for orphaned document references (document deleted)
            if (block.type === 'document_reference' && block.documentId) {
              if (!documentMap.has(block.documentId)) {
                issues.push({
                  id: `orphaned-doc-ref-${day.id}-${block.id || block.documentId}`,
                  title: 'Orphaned Document Link in Day',
                  description: `Day "${day.title || `Day ${day.dayNumber || day.order + 1}`}" contains a link to a deleted document.`,
                  type: 'orphaned-day-reference',
                  severity: 'medium',
                  entityType: 'day',
                  entityId: day.id,
                  canAutoFix: true,
                  details: {
                    blockType: 'document_reference',
                    blockId: block.id,
                    invalidId: block.documentId
                  }
                });
              } else {
                // Document exists - check for one-way link (Dayâ†’Doc but not Docâ†’Day)
                const doc = documentMap.get(block.documentId);
                const docConnections = doc.connections || [];
                const hasReciprocalLink = docConnections.some(conn =>
                  conn.entityType === 'day' && conn.entityId === day.id
                ) || registryLinkedPairs.has([day.id, block.documentId].sort().join('|'));

                if (!hasReciprocalLink) {
                  issues.push({
                    id: `oneway-doc-link-${day.id}-${block.documentId}`,
                    title: 'One-Way Document Link',
                    description: `Day "${day.title || `Day ${day.dayNumber || day.order + 1}`}" links to document "${doc.title || doc.name}", but the document doesn't link back.`,
                    type: 'oneway-link',
                    severity: 'low',
                    entityType: 'day',
                    entityId: day.id,
                    canAutoFix: true,
                    details: {
                      blockType: 'document_reference',
                      blockId: block.id,
                      documentId: block.documentId,
                      documentTitle: doc.title || doc.name
                    }
                  });
                }
              }
            }
          });
        });
      });

      // 5.5 Check for Orphaned Entity References in Place Content Blocks
      onProgress('Checking place content blocks for orphaned references...', 77);
      runSection('place content-block orphaned-reference check', () => {
        places.forEach(place => {
          const contentBlocks = place.contentBlocks || [];

          contentBlocks.forEach(block => {
            // Check blocks with sourceEntityType format (accommodation/travel links in places)
            if (block.sourceEntityType === 'accommodation' && block.sourceEntityId) {
              if (!accommodationMap.has(block.sourceEntityId)) {
                issues.push({
                  id: `orphaned-accom-in-place-${place.id}-${block.sourceEntityId}`,
                  title: 'Orphaned Accommodation Link in Place',
                  description: `Place "${place.name}" contains a link to deleted accommodation "${block.sourceEntityName || 'Unknown'}".`,
                  type: 'orphaned-content-block',
                  severity: 'medium',
                  entityType: 'place',
                  entityId: place.id,
                  canAutoFix: true,
                  details: {
                    sourceEntityType: block.sourceEntityType,
                    sourceEntityId: block.sourceEntityId,
                    sourceEntityName: block.sourceEntityName
                  }
                });
              }
            }

            if (block.sourceEntityType === 'travel' && block.sourceEntityId) {
              if (!travelMap.has(block.sourceEntityId)) {
                issues.push({
                  id: `orphaned-travel-in-place-${place.id}-${block.sourceEntityId}`,
                  title: 'Orphaned Travel Link in Place',
                  description: `Place "${place.name}" contains a link to deleted travel "${block.sourceEntityName || 'Unknown'}".`,
                  type: 'orphaned-content-block',
                  severity: 'medium',
                  entityType: 'place',
                  entityId: place.id,
                  canAutoFix: true,
                  details: {
                    sourceEntityType: block.sourceEntityType,
                    sourceEntityId: block.sourceEntityId,
                    sourceEntityName: block.sourceEntityName
                  }
                });
              }
            }

            // Check standard reference types in places
            if (block.type === 'accommodation_reference' && block.accommodationId) {
              if (!accommodationMap.has(block.accommodationId)) {
                issues.push({
                  id: `orphaned-accom-ref-${place.id}-${block.accommodationId}`,
                  title: 'Orphaned Accommodation Link in Place',
                  description: `Place "${place.name}" contains a link to a deleted accommodation.`,
                  type: 'orphaned-content-block',
                  severity: 'medium',
                  entityType: 'place',
                  entityId: place.id,
                  canAutoFix: true,
                  details: {
                    blockType: 'accommodation_reference',
                    blockId: block.id,
                    invalidId: block.accommodationId
                  }
                });
              }
            }

            if (block.type === 'travel_reference' && block.travelId) {
              if (!travelMap.has(block.travelId)) {
                issues.push({
                  id: `orphaned-travel-ref-${place.id}-${block.travelId}`,
                  title: 'Orphaned Travel Link in Place',
                  description: `Place "${place.name}" contains a link to a deleted travel item.`,
                  type: 'orphaned-content-block',
                  severity: 'medium',
                  entityType: 'place',
                  entityId: place.id,
                  canAutoFix: true,
                  details: {
                    blockType: 'travel_reference',
                    blockId: block.id,
                    invalidId: block.travelId
                  }
                });
              }
            }

            if (block.type === 'day_reference' && block.dayId) {
              if (!dayMap.has(block.dayId)) {
                issues.push({
                  id: `orphaned-day-ref-${place.id}-${block.dayId}`,
                  title: 'Orphaned Day Link in Place',
                  description: `Place "${place.name}" contains a link to a deleted day.`,
                  type: 'orphaned-content-block',
                  severity: 'medium',
                  entityType: 'place',
                  entityId: place.id,
                  canAutoFix: true,
                  details: {
                    blockType: 'day_reference',
                    blockId: block.id,
                    invalidId: block.dayId
                  }
                });
              }
            }

            if (block.type === 'document_reference' && block.documentId) {
              if (!documentMap.has(block.documentId)) {
                issues.push({
                  id: `orphaned-doc-ref-${place.id}-${block.documentId}`,
                  title: 'Orphaned Document Link in Place',
                  description: `Place "${place.name}" contains a link to a deleted document.`,
                  type: 'orphaned-content-block',
                  severity: 'medium',
                  entityType: 'place',
                  entityId: place.id,
                  canAutoFix: true,
                  details: {
                    blockType: 'document_reference',
                    blockId: block.id,
                    invalidId: block.documentId
                  }
                });
              }
            }
          });
        });
      });

      // 5.6 Check for Orphaned Entity References in Accommodation Content Blocks
      onProgress('Checking accommodation content blocks...', 78);
      runSection('accommodation content-block orphaned-reference check', () => {
        accommodations.forEach(acc => {
          const contentBlocks = acc.contentBlocks || [];

          contentBlocks.forEach(block => {
            if (block.type === 'day_reference' && block.dayId) {
              if (!dayMap.has(block.dayId)) {
                issues.push({
                  id: `orphaned-day-ref-acc-${acc.id}-${block.dayId}`,
                  title: 'Orphaned Day Link in Accommodation',
                  description: `Accommodation "${acc.summary || 'Unnamed'}" contains a link to a deleted day.`,
                  type: 'orphaned-content-block',
                  severity: 'medium',
                  entityType: 'accommodation',
                  entityId: acc.id,
                  canAutoFix: true,
                  details: {
                    blockType: 'day_reference',
                    blockId: block.id,
                    invalidId: block.dayId
                  }
                });
              }
            }

            if (block.type === 'document_reference' && block.documentId) {
              if (!documentMap.has(block.documentId)) {
                issues.push({
                  id: `orphaned-doc-ref-acc-${acc.id}-${block.documentId}`,
                  title: 'Orphaned Document Link in Accommodation',
                  description: `Accommodation "${acc.summary || 'Unnamed'}" contains a link to a deleted document.`,
                  type: 'orphaned-content-block',
                  severity: 'medium',
                  entityType: 'accommodation',
                  entityId: acc.id,
                  canAutoFix: true,
                  details: {
                    blockType: 'document_reference',
                    blockId: block.id,
                    invalidId: block.documentId
                  }
                });
              }
            }

            if (block.type === 'place_reference' && block.placeId) {
              if (!placeMap.has(block.placeId)) {
                issues.push({
                  id: `orphaned-place-ref-acc-${acc.id}-${block.placeId}`,
                  title: 'Orphaned Place Link in Accommodation',
                  description: `Accommodation "${acc.summary || 'Unnamed'}" contains a link to a deleted place.`,
                  type: 'orphaned-content-block',
                  severity: 'medium',
                  entityType: 'accommodation',
                  entityId: acc.id,
                  canAutoFix: true,
                  details: {
                    blockType: 'place_reference',
                    blockId: block.id,
                    invalidId: block.placeId
                  }
                });
              }
            }
          });
        });
      });

      // 5.7 Check for Orphaned Entity References in Travel Content Blocks
      onProgress('Checking travel content blocks...', 79);
      runSection('travel content-block orphaned-reference check', () => {
        travelItems.forEach(travel => {
          const contentBlocks = travel.contentBlocks || [];

          contentBlocks.forEach(block => {
            if (block.type === 'day_reference' && block.dayId) {
              if (!dayMap.has(block.dayId)) {
                issues.push({
                  id: `orphaned-day-ref-travel-${travel.id}-${block.dayId}`,
                  title: 'Orphaned Day Link in Travel',
                  description: `Travel "${travel.summary || 'Unnamed'}" contains a link to a deleted day.`,
                  type: 'orphaned-content-block',
                  severity: 'medium',
                  entityType: 'travel',
                  entityId: travel.id,
                  canAutoFix: true,
                  details: {
                    blockType: 'day_reference',
                    blockId: block.id,
                    invalidId: block.dayId
                  }
                });
              }
            }

            if (block.type === 'document_reference' && block.documentId) {
              if (!documentMap.has(block.documentId)) {
                issues.push({
                  id: `orphaned-doc-ref-travel-${travel.id}-${block.documentId}`,
                  title: 'Orphaned Document Link in Travel',
                  description: `Travel "${travel.summary || 'Unnamed'}" contains a link to a deleted document.`,
                  type: 'orphaned-content-block',
                  severity: 'medium',
                  entityType: 'travel',
                  entityId: travel.id,
                  canAutoFix: true,
                  details: {
                    blockType: 'document_reference',
                    blockId: block.id,
                    invalidId: block.documentId
                  }
                });
              }
            }

            if (block.type === 'place_reference' && block.placeId) {
              if (!placeMap.has(block.placeId)) {
                issues.push({
                  id: `orphaned-place-ref-travel-${travel.id}-${block.placeId}`,
                  title: 'Orphaned Place Link in Travel',
                  description: `Travel "${travel.summary || 'Unnamed'}" contains a link to a deleted place.`,
                  type: 'orphaned-content-block',
                  severity: 'medium',
                  entityType: 'travel',
                  entityId: travel.id,
                  canAutoFix: true,
                  details: {
                    blockType: 'place_reference',
                    blockId: block.id,
                    invalidId: block.placeId
                  }
                });
              }
            }

            if (block.type === 'accommodation_reference' && block.accommodationId) {
              if (!accommodationMap.has(block.accommodationId)) {
                issues.push({
                  id: `orphaned-accom-ref-travel-${travel.id}-${block.accommodationId}`,
                  title: 'Orphaned Accommodation Link in Travel',
                  description: `Travel "${travel.summary || 'Unnamed'}" contains a link to a deleted accommodation.`,
                  type: 'orphaned-content-block',
                  severity: 'medium',
                  entityType: 'travel',
                  entityId: travel.id,
                  canAutoFix: true,
                  details: {
                    blockType: 'accommodation_reference',
                    blockId: block.id,
                    invalidId: block.accommodationId
                  }
                });
              }
            }
          });
        });
      });

      // 6. Check for Date Range Conflicts (Basic)

      onProgress('Checking date consistency...', 80);
      runSection('date-range conflict check', () => {
        if (trip.startDate && trip.endDate) {
          const tripStart = new Date(trip.startDate);
          const tripEnd = new Date(trip.endDate);

          accommodations.forEach(acc => {
            if (acc.details?.checkInDate) {
              const checkIn = new Date(acc.details.checkInDate);
              if (checkIn < tripStart || checkIn > tripEnd) {
                issues.push({
                  id: `date-conflict-acc-${acc.id}`,
                  title: 'Date Out of Range',
                  description: `Accommodation "${acc.summary || 'Unnamed'}" check-in date is outside the trip dates.`,
                  type: 'date-conflict',
                  severity: 'low',
                  entityType: 'accommodation',
                  entityId: acc.id,
                  canAutoFix: false
                });
              }
            }
          });
        }
      });

      // 7. Connection registry parity / accommodation-day link audit
      onProgress('Checking connection registry parity...', 85);
      try {
        const registryConfig = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
        if (shouldRouteThroughRegistry(registryConfig)) {
          const auditReport = await tripLinkAuditService.auditTrip(userId, tripId);
          issues.push(...tripLinkAuditService.toHealthCheckIssues(auditReport));
        }
      } catch (registryAuditError) {
        console.warn('Connection registry audit skipped due to error:', registryAuditError);
        issues.push({
          id: 'registry-audit-failed',
          title: 'Connection Registry Audit Failed',
          description: 'Could not complete registry parity audit for this trip.',
          type: 'registry-parity',
          severity: 'low',
          canAutoFix: false,
          details: { message: registryAuditError.message }
        });
      }

      // 8. Registry integrity — hotels missing a located_in row, and dangling links
      // (Issue 12). The parity audit above only compares registry vs legacy content
      // blocks; it never asks "does this hotel have a located_in?" or "does this
      // registry row point at a deleted item?" — so a hotel a broken suggestion left
      // unlinked (Issue 1, now fixed going forward, but pre-existing damage remains)
      // passed every prior check.
      onProgress('Checking registry integrity...', 87);
      try {
        const registryConfig = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
        if (shouldRouteThroughRegistry(registryConfig)) {
          const accommodationsWithLocatedIn = new Set();
          allActiveLinks.forEach(link => {
            if (link.linkType !== 'located_in') return;
            const accEndpoint = link.entityLow?.type === 'accommodation' ? link.entityLow : link.entityHigh;
            if (accEndpoint?.id) accommodationsWithLocatedIn.add(accEndpoint.id);
          });

          accommodations
            .filter(acc => acc.details?.city && !accommodationsWithLocatedIn.has(acc.id))
            .forEach(acc => {
              issues.push({
                id: `missing-located-in-${acc.id}`,
                title: 'Hotel Not Linked to Its City',
                description: `"${acc.summary || acc.details?.name || 'Unnamed accommodation'}" has a city (${acc.details.city}) but no registry link to a place.`,
                type: 'missing-located-in',
                severity: 'medium',
                entityType: 'accommodation',
                entityId: acc.id,
                canAutoFix: false,
                details: { city: acc.details.city }
              });
            });

          const entityExists = (type, id) => {
            switch (type) {
            case 'accommodation': return accommodationMap.has(id);
            case 'place': return placeMap.has(id);
            case 'travel': return travelMap.has(id);
            case 'day': return dayMap.has(id);
            case 'document': return documentMap.has(id);
            default: return true; // unknown type — don't false-flag
            }
          };

          allActiveLinks
            .filter(link => {
              const low = link.entityLow;
              const high = link.entityHigh;
              return !low || !high || !entityExists(low.type, low.id) || !entityExists(high.type, high.id);
            })
            .forEach(link => {
              issues.push({
                id: `dangling-link-${link.linkId || link.id}`,
                title: 'Registry Link Points to a Deleted Item',
                description: `A ${link.linkType} link references an item that no longer exists.`,
                type: 'dangling-registry-link',
                severity: 'low',
                canAutoFix: false,
                details: { linkId: link.linkId || link.id, linkType: link.linkType, entityLow: link.entityLow, entityHigh: link.entityHigh }
              });
            });

          // Self-referential links: both endpoints are the same entity. Never a valid
          // state — safe to remove outright, no guessing which side is "correct".
          allActiveLinks
            .filter(link => {
              const low = link.entityLow;
              const high = link.entityHigh;
              return low && high && low.type === high.type && low.id === high.id;
            })
            .forEach(link => {
              issues.push({
                id: `self-referential-link-${link.linkId || link.id}`,
                title: 'Self-Referential Registry Link',
                description: `A ${link.linkType} link connects a ${link.entityLow.type} to itself.`,
                type: 'self-referential-link',
                severity: 'medium',
                canAutoFix: true,
                details: { linkId: link.linkId || link.id, linkType: link.linkType, entityLow: link.entityLow, entityHigh: link.entityHigh }
              });
            });

          // A day can only be staying at one accommodation. Multiple active stays_on
          // links to different accommodations for the same day is a genuine conflict —
          // flag it for review rather than guess which one to keep.
          const staysOnByDay = new Map();
          allActiveLinks.forEach(link => {
            if (link.linkType !== 'stays_on') return;
            const dayEndpoint = link.entityLow?.type === 'day' ? link.entityLow : link.entityHigh;
            const accEndpoint = link.entityLow?.type === 'accommodation' ? link.entityLow : link.entityHigh;
            if (!dayEndpoint?.id || !accEndpoint?.id) return;
            if (!staysOnByDay.has(dayEndpoint.id)) staysOnByDay.set(dayEndpoint.id, []);
            staysOnByDay.get(dayEndpoint.id).push({ link, accommodationId: accEndpoint.id });
          });

          staysOnByDay.forEach((entries, dayId) => {
            const uniqueAccommodationIds = [...new Set(entries.map(entry => entry.accommodationId))];
            if (uniqueAccommodationIds.length <= 1) return;

            const day = dayMap.get(dayId);
            const dayLabel = day?.date || dayId;
            const accommodationNames = uniqueAccommodationIds.map(id => (
              accommodationMap.get(id)?.details?.name || accommodationMap.get(id)?.summary || id
            ));
            issues.push({
              id: `conflicting-stays-on-${dayId}`,
              title: 'Day Linked to Multiple Accommodations',
              description: `Day ${dayLabel} has active stay links to ${uniqueAccommodationIds.length} different accommodations (${accommodationNames.join(', ')}) — a day can only stay at one.`,
              type: 'conflicting-stays-on',
              severity: 'high',
              entityType: 'day',
              entityId: dayId,
              canAutoFix: false,
              details: {
                dayId,
                accommodationIds: uniqueAccommodationIds,
                linkIds: entries.map(entry => entry.link.linkId || entry.link.id)
              }
            });
          });
        }
      } catch (registryIntegrityError) {
        console.warn('Registry integrity check skipped due to error:', registryIntegrityError);
      }

      onProgress('Finalizing report...', 90);

      // Simulate a small delay for better UX if it was too fast
      if (issues.length === 0) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      onProgress('Done', 100);
      return issues;

    } catch (error) {
      console.error('Health check failed:', error);
      throw error;
    }
  },

  /**
     * Attempts to fix a specific issue.
     * @param {string} userId 
     * @param {string} tripId 
     * @param {Object} issue - The issue object to fix.
     * @returns {Promise<boolean>} True if fixed, false otherwise.
     */
  async fixIssue(userId, tripId, issue) {
    try {
      if (!issue.canAutoFix) return false;

      if (issue.type === 'broken-link') {
        if (issue.entityType === 'accommodation') {
          // Remove the broken place reference
          await updateAccommodation(userId, tripId, issue.entityId, {
            placeReference: null
          });
          return true;
        } else if (issue.entityType === 'travel') {
          // Remove the broken place reference
          const updateData = {};
          if (issue.details.field === 'departurePlace') updateData.departurePlace = null;
          if (issue.details.field === 'arrivalPlace') updateData.arrivalPlace = null;

          await updateTravel(userId, tripId, issue.entityId, updateData);
          return true;
        }
      }

      // Handle orphaned entity references in day content
      if (issue.type === 'orphaned-day-reference' && issue.entityType === 'day') {
        // Fetch the day
        const allDays = await getDays(userId, tripId);
        const day = allDays.find(d => d.id === issue.entityId);

        if (day && Array.isArray(day.content)) {
          // Filter out the orphaned content block
          const { blockType, invalidId } = issue.details;
          const updatedContent = day.content.filter(block => {
            if (blockType === 'travel_reference' && block.type === 'travel_reference') {
              return block.travelId !== invalidId;
            }
            if (blockType === 'accommodation_reference' && block.type === 'accommodation_reference') {
              return block.accommodationId !== invalidId;
            }
            if (blockType === 'place_reference' && block.type === 'place_reference') {
              return block.placeId !== invalidId;
            }
            if (blockType === 'document_reference' && block.type === 'document_reference') {
              return block.documentId !== invalidId;
            }
            return true;
          });

          // Also check contentBlocks for document references
          let updatedContentBlocks = day.contentBlocks || [];
          if (blockType === 'document_reference' && Array.isArray(updatedContentBlocks)) {
            updatedContentBlocks = updatedContentBlocks.filter(block =>
              !(block.type === 'document_reference' && block.documentId === invalidId)
            );
          }

          await updateDay(userId, tripId, issue.entityId, {
            content: updatedContent,
            contentBlocks: updatedContentBlocks
          });
          return true;
        }
      }

      // Handle one-way document links (Issue 23). Both the day and the document still
      // exist at this point (detection only reaches 'oneway-link' when the doc lookup
      // succeeded) — the visible link the user sees is real, so prefer completing the
      // reciprocal side over deleting what's already there.
      if (issue.type === 'oneway-link' && issue.entityType === 'day') {
        const { documentId } = issue.details;
        const registryConfig = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);

        if (shouldRouteThroughRegistry(registryConfig)) {
          await connectionRegistryService.createLink(
            userId,
            tripId,
            { type: 'day', id: issue.entityId },
            { type: 'document', id: documentId },
            {
              linkType: LINK_TYPES.DOCUMENT_FOR,
              metadata: { createdBy: 'health_check_autofix' },
              skipLegacyWrite: registryConfig.writeMode === 'registry_only'
            }
          );
        } else {
          await documentService.addConnection(userId, tripId, documentId, {
            entityType: 'day',
            entityId: issue.entityId,
            connectionType: 'related'
          });
        }
        return true;
      }

      // Handle orphaned content blocks in Places, Travel, and Accommodation
      if (issue.type === 'orphaned-content-block') {
        const { sourceEntityId, sourceEntityType, blockType, invalidId } = issue.details;

        if (issue.entityType === 'place') {
          const allPlaces = await getPlaces(userId, tripId);
          const place = allPlaces.find(p => p.id === issue.entityId);

          if (place && Array.isArray(place.contentBlocks)) {
            const updatedContentBlocks = place.contentBlocks.filter(block => {
              // Filter by sourceEntityId (Places use this format for accommodation/travel links)
              if (sourceEntityId && block.sourceEntityId === sourceEntityId) {
                return false;
              }
              // Filter by standard reference type
              if (blockType && invalidId) {
                if (blockType === 'accommodation_reference' && block.accommodationId === invalidId) return false;
                if (blockType === 'travel_reference' && block.travelId === invalidId) return false;
                if (blockType === 'day_reference' && block.dayId === invalidId) return false;
                if (blockType === 'document_reference' && block.documentId === invalidId) return false;
              }
              return true;
            });

            await updatePlace(userId, tripId, issue.entityId, {
              contentBlocks: updatedContentBlocks,
              contentBlocksLastUpdated: new Date().toISOString()
            });
            return true;
          }
        }

        if (issue.entityType === 'accommodation') {
          const allAccommodations = await getAccommodation(userId, tripId);
          const acc = allAccommodations.find(a => a.id === issue.entityId);

          if (acc && Array.isArray(acc.contentBlocks)) {
            const updatedContentBlocks = acc.contentBlocks.filter(block => {
              if (blockType && invalidId) {
                if (blockType === 'day_reference' && block.dayId === invalidId) return false;
                if (blockType === 'document_reference' && block.documentId === invalidId) return false;
                if (blockType === 'place_reference' && block.placeId === invalidId) return false;
              }
              return true;
            });

            await updateAccommodation(userId, tripId, issue.entityId, {
              contentBlocks: updatedContentBlocks
            });
            return true;
          }
        }

        if (issue.entityType === 'travel') {
          const allTravel = await getTravel(userId, tripId);
          const travel = allTravel.find(t => t.id === issue.entityId);

          if (travel && Array.isArray(travel.contentBlocks)) {
            const updatedContentBlocks = travel.contentBlocks.filter(block => {
              if (blockType && invalidId) {
                if (blockType === 'day_reference' && block.dayId === invalidId) return false;
                if (blockType === 'document_reference' && block.documentId === invalidId) return false;
                if (blockType === 'place_reference' && block.placeId === invalidId) return false;
                if (blockType === 'accommodation_reference' && block.accommodationId === invalidId) return false;
              }
              return true;
            });

            await updateTravel(userId, tripId, issue.entityId, {
              contentBlocks: updatedContentBlocks
            });
            return true;
          }
        }
      }

      if (issue.type === 'registry-parity' && issue.canAutoFix) {
        const result = await connectionRegistryService.reconcileLegacyParity(userId, tripId, {
          dryRun: false
        });
        return result.errors.length === 0;
      }

      if (issue.type === 'registry-link-audit' && issue.canAutoFix) {
        await tripLinkAuditService.fixAccommodationDayLinkIssues(userId, tripId, {
          dryRun: false
        });
        return true;
      }

      if (issue.type === 'self-referential-link' && issue.canAutoFix) {
        await connectionRegistryService.removeLink(userId, tripId, issue.details.linkId, {
          hardDelete: true
        });
        return true;
      }

      return false;
    } catch (error) {
      console.error(`Failed to fix issue ${issue.id}:`, error);
      throw error;
    }
  }
};
